/* eslint-disable react-refresh/only-export-components */
import {
  DimeDatePicker,
  DimeDateRangePicker,
  DimeDateTimePicker,
  DimeDurationPicker,
} from '@nudool/design-system';
import type { DateRange } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { CardDocsSection as DocsSection } from './-CardDocsSection.js';
import { CalendarDayDocsSection } from './-DatePickerCalendarDaySection.js';

export const Route = createFileRoute('/design-system/date-picker')({
  component: DatePickerPage,
});

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function BasicExample() {
  const [date, setDate] = React.useState<Date | undefined>();
  return <DimeDatePicker value={date} onValueChange={setDate} />;
}

function RangeExample() {
  const [range, setRange] = React.useState<DateRange | undefined>();
  return <DimeDateRangePicker value={range} onValueChange={setRange} />;
}

function ClearableRangeExample() {
  const [range, setRange] = React.useState<DateRange | undefined>();
  return (
    <DimeDateRangePicker
      value={range}
      onValueChange={setRange}
      placeholder="Any date"
      clearLabel="Clear dates"
    />
  );
}

function FilterRangeExample() {
  const [range, setRange] = React.useState<DateRange | undefined>();
  return <DimeDateRangePicker appearance="filter" value={range} onValueChange={setRange} />;
}

function DateOfBirthExample() {
  const [date, setDate] = React.useState<Date | undefined>();
  return (
    <DimeDatePicker
      value={date}
      onValueChange={setDate}
      placeholder="Date of birth"
      calendarProps={{ captionLayout: 'dropdown' }}
    />
  );
}

function DateTimeExample() {
  const [date, setDate] = React.useState<Date | undefined>();
  return <DimeDateTimePicker value={date} onValueChange={setDate} defaultTime="09:00" />;
}

function DurationExample() {
  const [minutes, setMinutes] = React.useState<number | null>(90);
  return (
    <DimeDurationPicker value={minutes} onValueChange={setMinutes} hoursLabel="Duration in hours" />
  );
}

function DurationInDaysExample() {
  const [minutes, setMinutes] = React.useState<number | null>(null);
  return (
    <DimeDurationPicker value={minutes} onValueChange={setMinutes} maxDays={7} hoursLabel="Hours" />
  );
}

function DatePickerPage() {
  return (
    <div className="max-w-4xl">
      <h1 className="mb-1 text-3xl font-bold">Date Picker</h1>
      <p className="mb-8 text-muted-foreground">
        A button-triggered date picker composed from{' '}
        <code className="font-mono text-sm">DimePopover</code> +{' '}
        <code className="font-mono text-sm">DimeCalendar</code>. Use{' '}
        <code className="font-mono text-sm">DimeDateRangePicker</code> for start/end ranges.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">value</code> and{' '}
          <code className="font-mono">onValueChange</code> for controlled usage, or{' '}
          <code className="font-mono">defaultValue</code> for uncontrolled. Override the button
          label with <code className="font-mono">placeholder</code>.
        </p>
      </DocsSection>

      <CalendarDayDocsSection />

      <DocsSection title="Range picker">
        <Preview>
          <RangeExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeDateRangePicker</code> accepts{' '}
          <code className="font-mono">{'value: { from?: Date; to?: Date }'}</code>. The popover
          stays open after the first date click and closes on the second; click the same day twice
          for a one-day range.
        </p>
      </DocsSection>

      <DocsSection title="Clearable range">
        <Preview>
          <ClearableRangeExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The calendar never clears a picked range on its own, so an optional range passes{' '}
          <code className="font-mono">clearLabel</code> to show a button, once a range is set, that
          empties it back to <code className="font-mono">undefined</code> and the{' '}
          <code className="font-mono">placeholder</code>.
        </p>
      </DocsSection>

      <DocsSection title="Filter appearance">
        <Preview>
          <FilterRangeExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">appearance=&quot;filter&quot;</code> puts the range on a
          list-toolbar filter button, as on the customer Events tab.
        </p>
      </DocsSection>

      <DocsSection title="Date of birth">
        <Preview>
          <DateOfBirthExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">{'calendarProps={{ captionLayout: "dropdown" }}'}</code>{' '}
          to show month/year dropdowns — useful for navigating to a specific birth year without
          clicking through months.
        </p>
      </DocsSection>

      <DocsSection title="Date and time">
        <Preview>
          <DateTimeExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeDateTimePicker</code> picks a day and a time together.
          Pass <code className="font-mono">defaultTime</code> as{' '}
          <code className="font-mono">HH:mm</code> for the time used when a day is picked before any
          time is set. It is deprecated: it reads and writes a{' '}
          <code className="font-mono">Date</code> in the viewer&apos;s zone, so a time for a place
          in another zone drifts. For a time at a place, pair{' '}
          <code className="font-mono">DimeDatePicker valueFormat=&quot;ymd&quot;</code> with a time
          input and a zone, as the event wizard does.
        </p>
      </DocsSection>

      <DocsSection title="Duration">
        <Preview>
          <DurationExample />
          <DurationInDaysExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeDurationPicker</code> holds a length in minutes. Pass{' '}
          <code className="font-mono">maxDays</code> to add a days select and cap the total, and{' '}
          <code className="font-mono">minuteStep</code> to change the spacing of the minute options.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeDatePicker disabled />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> to prevent interaction.
        </p>
      </DocsSection>
    </div>
  );
}
